import {
  Anchor,
  Badge,
  Checkbox,
  Group,
  Paper,
  Popover,
  Stack,
  Text
} from '@mantine/core'
import { IconRepeat } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  LabelGroupPopover,
  LabelGroupPopoverButtons,
  LabelGroupPopoverHeader
} from '../LabelGroupPopover/LabelGroupPopover'

type RepeatedAlertConfigProps = {
  isEnabled: boolean
  onChange: (isEnabled: boolean) => void
}

export const RepeatedAlertConfig = ({
  isEnabled,
  onChange
}: RepeatedAlertConfigProps) => {
  const [isPopoverOpened, setIsPopoverOpened] = useState(false)
  const [cachedIsEnabled, setCachedIsEnabled] = useState(isEnabled)

  useEffect(() => {
    setCachedIsEnabled(isEnabled)
  }, [isEnabled])

  const resetAndClose = () => {
    setIsPopoverOpened(false)
    setCachedIsEnabled(isEnabled)
  }

  const handlePopoverChange = (opened: boolean) => {
    if (!opened) {
      resetAndClose()
    } else {
      setIsPopoverOpened(opened)
    }
  }

  const handleSave = () => {
    onChange(cachedIsEnabled)
    setIsPopoverOpened(false)
  }

  return (
    <LabelGroupPopover opened={isPopoverOpened} onChange={handlePopoverChange}>
      <Popover.Target>
        <Anchor
          component="button"
          type="button"
          size="xs"
          styles={{
            root: {
              textTransform: 'lowercase'
            }
          }}
          onClick={() => handlePopoverChange(!isPopoverOpened)}
        >
          <Group wrap="nowrap" gap={4} align="center">
            <IconRepeat size={12} />

            <FormattedMessage id="logic.alert.repeated" />

            <Badge
              size="xs"
              fz={8}
              color={isEnabled ? 'brand-primary' : 'gray'}
            >
              {isEnabled ? (
                <FormattedMessage id="logic.alert.yes" />
              ) : (
                <FormattedMessage id="logic.alert.no" />
              )}
            </Badge>
          </Group>
        </Anchor>
      </Popover.Target>

      <Popover.Dropdown>
        <LabelGroupPopoverHeader
          title={<FormattedMessage id="logic.alert.repeated" />}
          icon={<IconRepeat size={20} />}
          onClose={resetAndClose}
        />

        <PlatformAlert variant="info" mb="xl" compact>
          <Stack>
            <Text size="xs">
              <FormattedMessage id="logic.alert.repeated.info.line1" />
            </Text>

            <Text size="xs">
              <FormattedMessage id="logic.alert.repeated.info.line2" />
            </Text>
          </Stack>
        </PlatformAlert>

        <Paper bg="gray.1" p="sm">
          <Checkbox
            checked={cachedIsEnabled}
            label={
              <FormattedMessage id="logic.alert.repeated.checkbox.label" />
            }
            onChange={(event) =>
              setCachedIsEnabled(event.currentTarget.checked)
            }
          />
        </Paper>

        <LabelGroupPopoverButtons
          onCancel={resetAndClose}
          onSave={handleSave}
        />
      </Popover.Dropdown>
    </LabelGroupPopover>
  )
}
